package fr.manyverse.app.presentation.viewmodel

import fr.manyverse.app.presentation.error.toUserMessage
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

/**
 * Scope-based equivalents of the [BaseViewModel] async helpers.
 *
 * They let stateful collaborators extracted out of a ViewModel (e.g. the GameMaster
 * delegates) reuse the exact same Loading/Success/Error transitions while operating on
 * the ViewModel's `viewModelScope`, without having to be ViewModels themselves.
 */

fun <T> CoroutineScope.launchAsync(
    state: MutableStateFlow<AsyncUiState<T>>,
    block: suspend () -> Result<T>
): Job = launch {
    state.value = AsyncUiState.Loading
    block()
        .onSuccess { state.value = AsyncUiState.Success(it) }
        .onFailure { state.value = AsyncUiState.Error(it.toUserMessage()) }
}

fun CoroutineScope.launchAction(
    state: MutableStateFlow<AsyncUiState<Unit>>,
    block: suspend () -> Result<Unit>
): Job = launchAsync(state, block)

fun <T> CoroutineScope.launchAsyncWithCallback(
    state: MutableStateFlow<AsyncUiState<T>>,
    block: suspend () -> Result<T>,
    onSuccess: suspend (T) -> Unit = {}
): Job = launch {
    state.value = AsyncUiState.Loading
    block()
        .onSuccess {
            state.value = AsyncUiState.Success(it)
            onSuccess(it)
        }
        .onFailure { state.value = AsyncUiState.Error(it.toUserMessage()) }
}

fun <T, R> CoroutineScope.launchAsyncMapped(
    state: MutableStateFlow<AsyncUiState<R>>,
    block: suspend () -> Result<T>,
    transform: (T) -> R
): Job = launch {
    state.value = AsyncUiState.Loading
    block()
        .onSuccess { state.value = AsyncUiState.Success(transform(it)) }
        .onFailure { state.value = AsyncUiState.Error(it.toUserMessage()) }
}
